import { Box, Chip, Typography } from "@mui/material";
import ConfigurableTable, { TableSortType } from "components/ConfigurableTable";
import BasicActionsThreeDotsMenu from "components/ThreeDots/BasicActionsThreeDotsMenu";
import { formatQualification } from "helpers/FormatHelpers";
import { MRT_ColumnDef } from "material-react-table";
import { useIntl } from "react-intl";
import {
  SkuQualification,
  SkuQualificationView,
} from "services/KnowfullyAdminClient";
import { MultiValueCell } from "../MultiValueCell";
import SkuQualificationStatusChip from "../SkuQualificationStatusChip";
import LegacyExpandedChildTable from "./LegacyExpandedChildTable";
import { groupQualificationsByState, LegacyQualificationGroup } from "./grouping";

// The Accreditation Bodies grid as it stood before the grouped view. Kept whole so the
// organizations without the grouped view enabled see no change at all; delete this folder
// along with the setting once every organization is switched over.
type LegacyQualificationsTableProps = {
  readonly qualifications: SkuQualificationView[];
  readonly isLoading: boolean;
  readonly isReadonly?: boolean;
  readonly onEdit: (qualification: LegacyQualificationGroup) => void;
  readonly onChildEdit: (qualification: SkuQualification) => void;
  readonly onDelete: (qualification: SkuQualification) => void;
  readonly onOrderChange: (order: TableSortType) => void;
  readonly onOrderByChange: (orderBy: string) => void;
};

const styles = {
  bottomToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    px: 1,
    py: 1.5,
  },
};

export default function LegacyQualificationsTable({
  qualifications,
  isLoading,
  isReadonly,
  onEdit,
  onChildEdit,
  onDelete,
  onOrderChange,
  onOrderByChange,
}: LegacyQualificationsTableProps) {
  const { formatMessage: t } = useIntl();
  const rows = groupQualificationsByState(qualifications);

  const columns: Array<MRT_ColumnDef<LegacyQualificationGroup>> = [
    {
      id: "verificationStatus.name",
      header: t({ id: "statusLabel" }),
      enableSorting: false,
      accessorFn: (row) => row,
      Cell: ({ row }) => <SkuQualificationStatusChip value={row.original} />,
    },
    {
      accessorKey: "qualification.value",
      header: t({ id: "accreditationBodyLabel" }),
      enableHiding: false,
      Cell: ({ row }) => formatQualification(row.original.qualification!),
    },
    {
      accessorKey: "profession.name",
      header: t({ id: "professionLabel" }),
      enableHiding: false,
    },
    {
      id: "state.stateName",
      accessorFn: (row) => row.stateName,
      header: t({ id: "locationStateLabel" }),
      enableHiding: false,
      Cell: ({ row }) => {
        const qual = row.original;
        if (!qual.isGroup) return <>{qual.stateName || ""}</>;

        const firstChild = qual.childQualifications?.[0];
        if (qual.stateCount === 1 && firstChild?.stateName) {
          return <>{firstChild.stateName}</>;
        }

        return (
          <Chip
            size="small"
            label={`${qual.stateCount} states`}
            color="primary"
          />
        );
      },
    },
    {
      accessorKey: "courseNumber",
      header: t({ id: "courseNumber" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.courseNumber} />,
    },
    {
      accessorKey: "approvalCode",
      header: t({ id: "approvalCode" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.approvalCode} />,
    },
    {
      accessorKey: "approvalDate",
      header: t({ id: "approvalDate" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.approvalDate} />,
    },
    {
      accessorKey: "releaseDate",
      header: t({ id: "skuFormReleaseDateLabel" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.releaseDate} />,
    },
    {
      accessorKey: "creditSubmissionExpirationDate",
      header: t({ id: "skuFormCreditSubmissionExpirationDateLabel" }),
      Cell: ({ row }) => (
        <MultiValueCell value={row.original.creditSubmissionExpirationDate} />
      ),
    },
    {
      accessorKey: "credits",
      header: t({ id: "credits" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.credits} />,
    },
    {
      accessorKey: "minimumCredit",
      header: t({ id: "minimumCredit" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.minimumCredit} />,
    },
    {
      accessorKey: "partialCreditIncrement",
      header: t({ id: "partialCreditIncrement" }),
      Cell: ({ row }) => (
        <MultiValueCell value={row.original.partialCreditIncrement} />
      ),
    },
  ];

  if (!isReadonly) {
    columns.push({
      id: "actions",
      header: " ",
      size: 50,
      enableSorting: false,
      enableResizing: false,
      enableColumnOrdering: false,
      enableHiding: false,
      accessorFn: (row) => row,
      Cell: ({ row }) => {
        const qual = row.original;
        return (
          <BasicActionsThreeDotsMenu
            onEdit={() => onEdit(qual)}
            onDelete={
              qual.isGroup ? undefined : () => onDelete(qual as SkuQualification)
            }
          />
        );
      },
    });
  }

  return (
    <ConfigurableTable
      isLoading={isLoading}
      settingsId="skuQualificationsTable"
      columns={columns}
      data={rows}
      enableExpanding={true}
      enableExpandAll={false}
      initialState={
        isReadonly ? undefined : { columnPinning: { right: ["actions"] } }
      }
      getRowCanExpand={(row) => {
        const original = row.original;
        return !!(
          original?.isGroup === true &&
          original?.childQualifications &&
          original.childQualifications.length > 1
        );
      }}
      renderDetailPanel={({ row }) => {
        const original = row.original;
        if (
          !original?.isGroup ||
          !original?.childQualifications ||
          original.childQualifications.length <= 1
        ) {
          return null;
        }
        return (
          <LegacyExpandedChildTable
            row={row}
            onEdit={onChildEdit}
            onDelete={onDelete}
            isReadonly={isReadonly}
          />
        );
      }}
      paginationSettings={{
        onOrderChange,
        onOrderByChange,
      }}
      renderBottomToolbar={() => (
        <Box sx={styles.bottomToolbar}>
          <Typography>Total: {qualifications.length}</Typography>
        </Box>
      )}
    />
  );
}
